import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ToolSidebar from "../utils/ToolSideBar";
import { API_URL, authFetch } from "../utils/auth";
import "../../css/staff/InternalWorkspace.css";

const nonFinalStatuses = new Set(["new", "under_review", "more_information_needed"]);

function Field({ label, children }) {
  return <div><dt>{label}</dt><dd>{children || "Not provided"}</dd></div>;
}

function DetailBlock({ title, children }) {
  return <section className="intake-detail-block"><h2>{title}</h2>{children}</section>;
}

function Modal({ title, children, footer, onClose, submitting, error }) {
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
    function onKeyDown(event) {
      if (event.key === "Escape" && !submitting) onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, submitting]);

  return (
    <div className="review-modal-backdrop" role="presentation">
      <section className="review-modal" role="dialog" aria-modal="true" aria-labelledby="review-modal-title">
        <header className="review-modal-header">
          <h2 id="review-modal-title">{title}</h2>
          <button ref={closeRef} type="button" aria-label="Close review action" onClick={onClose} disabled={submitting}>x</button>
        </header>
        {error && <p className="review-modal-error" role="alert">{error}</p>}
        <div className="review-modal-body">{children}</div>
        <footer className="review-modal-footer">{footer}</footer>
      </section>
    </div>
  );
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : "Not recorded";
}

function optionValue(value) {
  return value === null || value === undefined ? "" : String(value);
}

function nullableId(value) {
  return value === "" || value === null || value === undefined ? null : Number(value);
}

function validationMessage(data, fallback) {
  if (data?.error?.message) return data.error.message;
  if (data?.detail) return data.detail;
  if (data && typeof data === "object") {
    const firstKey = Object.keys(data).find((key) => Array.isArray(data[key]) || typeof data[key] === "string");
    if (firstKey) {
      const value = Array.isArray(data[firstKey]) ? data[firstKey].join(" ") : data[firstKey];
      return `${firstKey}: ${value}`;
    }
  }
  return fallback;
}

export default function ProjectRequestDetailPage() {
  const { requestId } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [modal, setModal] = useState("");
  const [modalError, setModalError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [controls, setControls] = useState({ project_work_type: "", review_status: "new", partner_id: "", reviewer_id: "", review_notes: "" });
  const [form, setForm] = useState({});

  const hydrateControls = useCallback((data) => {
    setControls({
      project_work_type: data.project_work_type || "",
      review_status: data.review_status || "new",
      partner_id: optionValue(data.referred_by_partner_id),
      reviewer_id: optionValue(data.assigned_reviewer_id),
      review_notes: data.review_notes || "",
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/staff/project-requests/${requestId}/`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Unable to load request.");
      setRequest(data);
      hydrateControls(data);
      setError("");
    } catch (err) {
      setError(err.message || "Unable to load request.");
    } finally {
      setLoading(false);
    }
  }, [hydrateControls, requestId]);

  useEffect(() => { load(); }, [load]);

  const finalDecision = request?.review_status === "approved" || request?.review_status === "declined";
  const reviewStatusOptions = useMemo(() => (request?.review_status_options || []).filter((item) => nonFinalStatuses.has(item.value) || item.value === request?.review_status), [request]);

  function openModal(name) {
    setModal(name);
    setModalError("");
    setForm({
      project_name: request?.business_name || "",
      partner_id: controls.partner_id,
      project_work_type: controls.project_work_type,
    });
  }

  function closeModal() {
    if (!submitting) {
      setModal("");
      setModalError("");
    }
  }

  async function requestAction(path, payload = {}, method = "POST", successMessage = "Request updated.") {
    setSubmitting(true);
    setModalError("");
    setNotice("");
    try {
      const response = await authFetch(`${API_URL}/staff/project-requests/${requestId}/${path}/`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(validationMessage(data, "Unable to update request."));
      setRequest(data);
      hydrateControls(data);
      setNotice(successMessage);
      setModal("");
      setForm({});
    } catch (err) {
      setModalError(err.message || "Unable to update request.");
    } finally {
      setSubmitting(false);
    }
  }

  function reviewPayload(overrides = {}) {
    const next = { ...controls, ...overrides };
    return {
      project_work_type: next.project_work_type || "",
      review_status: next.review_status || "new",
      partner_id: nullableId(next.partner_id),
      reviewer_id: nullableId(next.reviewer_id),
      review_notes: next.review_notes || "",
    };
  }

  async function patchReviewControls(payload, successMessage) {
    setSubmitting(true);
    setNotice("");
    setError("");
    try {
      const response = await authFetch(`${API_URL}/staff/project-requests/${requestId}/review-controls/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(validationMessage(data, "Unable to save review controls."));
      setRequest(data);
      hydrateControls(data);
      setNotice(successMessage);
      return data;
    } catch (err) {
      setError(err.message || "Unable to save review controls.");
      return null;
    } finally {
      setSubmitting(false);
    }
  }

  async function saveReview() {
    await patchReviewControls(reviewPayload(), "Review controls saved.");
  }

  async function assignToMe() {
    const currentUserId = request?.current_user?.id;
    if (!currentUserId) {
      setError("Unable to assign reviewer: current user id is unavailable.");
      return;
    }
    await patchReviewControls({ reviewer_id: currentUserId }, "Assigned to you for review.");
  }

  if (loading) return <div className="staff-shell"><ToolSidebar active="project-requests" /><main className="staff-page internal-page project-request-detail-page"><section className="staff-card"><p>Loading request...</p></section></main></div>;
  if (error || !request) return <div className="staff-shell"><ToolSidebar active="project-requests" /><main className="staff-page internal-page project-request-detail-page"><section className="staff-card"><p>{error || "Request not found."}</p></section></main></div>;

  return (
    <div className="staff-shell">
      <ToolSidebar active="project-requests" />
      <main className="staff-page internal-page project-request-detail-page">
        <header className="project-request-review-header">
          <div>
            <span className="review-eyebrow">{request.review_status_label || "Project Request"}</span>
            <h1>{request.business_name || request.client_name || "Project Request"}</h1>
            <p>{request.client_name} - Submitted {formatDate(request.submitted_at)} - Request #{request.id}</p>
          </div>
          <div className="request-header-actions">
            <span className={`review-pill review-${request.review_status}`}>{request.review_status_label}</span>
            <Link to="/project-requests">Back to requests</Link>
          </div>
        </header>

        {notice && <section className="staff-card request-notice"><p>{notice}</p></section>}
        {error && <section className="staff-card request-error"><p>{error}</p></section>}

        <section className="project-intake-review-layout">
          <article className="staff-card project-intake-review-main">
            <DetailBlock title="Client">
              <dl className="staff-definition-grid">
                <Field label="Name">{request.client_name}</Field>
                <Field label="Email">{request.email}</Field>
                <Field label="Phone">{request.phone}</Field>
                <Field label="Preferred Contact">{request.preferred_contact_method}</Field>
              </dl>
            </DetailBlock>

            <DetailBlock title="Request Overview">
              <dl className="staff-definition-grid">
                <Field label="Business">{request.business_name}</Field>
                <Field label="Industry">{request.industry}</Field>
                <Field label="Business Stage">{request.business_stage}</Field>
                <Field label="Requested Service">{request.requested_service}</Field>
                <Field label="Budget">{request.budget}</Field>
                <Field label="Timeline">{request.timeline}</Field>
                <Field label="Content Status">{request.content_status}</Field>
                <Field label="Current Website">{request.current_website}</Field>
                <Field label="Social Media">{request.social_media}</Field>
              </dl>
            </DetailBlock>

            <DetailBlock title="Project Description">
              {request.project_goal && <p><strong>Goal:</strong> {request.project_goal}</p>}
              <p className="preserve-lines">{request.project_description || "Not provided"}</p>
              {request.features?.length > 0 && <p><strong>Requested features:</strong> {request.features.join(", ")}</p>}
              {request.inspiration_links && <p className="preserve-lines"><strong>Inspiration:</strong>\n{request.inspiration_links}</p>}
            </DetailBlock>

            <DetailBlock title="Existing / Related Client Context">
              <dl className="staff-definition-grid">
                <Field label="Source">{request.source}</Field>
                <Field label="Assigned Partner">{request.referring_partner}</Field>
                <Field label="Client Account">{request.onboarding_email_sent_at ? "Invitation sent" : finalDecision ? "Invitation pending" : "Not created"}</Field>
                <Field label="Active Project">{request.active_project_path ? <Link to={request.active_project_path}>Open active project</Link> : "Not converted"}</Field>
              </dl>
            </DetailBlock>

            <DetailBlock title="Review Activity">
              {request.review_activity?.length ? request.review_activity.map((item) => <div className="workspace-row" key={item.id}><strong>{item.summary || item.action}</strong><span>{item.actor} - {formatDate(item.created_at)}</span></div>) : <p className="staff-empty inline">No review activity yet.</p>}
            </DetailBlock>
          </article>

          <aside className="staff-card project-review-controls" aria-label="Review controls">
            <div className="review-controls-heading">
              <span>HR / Superuser Workspace</span>
              <h2>Review Controls</h2>
            </div>

            <label htmlFor="project-work-type">Work Type</label>
            <select id="project-work-type" value={controls.project_work_type} onChange={(e) => setControls({ ...controls, project_work_type: e.target.value })} disabled={finalDecision}>
              <option value="">Select work type</option>
              {(request.project_work_type_options || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>

            <label htmlFor="review-status">Review Status</label>
            <select id="review-status" value={controls.review_status} onChange={(e) => setControls({ ...controls, review_status: e.target.value })} disabled={finalDecision}>
              {reviewStatusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>

            <label htmlFor="assigned-partner">Assigned Partner</label>
            <select id="assigned-partner" value={controls.partner_id} onChange={(e) => setControls({ ...controls, partner_id: e.target.value })}>
              <option value="">Unassigned</option>
              {(request.partner_options || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>

            <label htmlFor="assigned-reviewer">Assigned Reviewer</label>
            <div className="reviewer-control-row">
              <select id="assigned-reviewer" value={controls.reviewer_id} onChange={(e) => setControls({ ...controls, reviewer_id: e.target.value })}>
                <option value="">Unassigned</option>
                {(request.reviewer_options || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
              <button type="button" className="review-action secondary" onClick={assignToMe} disabled={submitting}>Assign to Me</button>
            </div>
            {request.assigned_reviewer && <small className="review-current-assignee">Current reviewer: {request.assigned_reviewer.name}</small>}

            <label htmlFor="review-notes">Review Notes</label>
            <textarea id="review-notes" value={controls.review_notes} onChange={(e) => setControls({ ...controls, review_notes: e.target.value })} />

            <div className="review-control-actions">
              <button type="button" className="review-action primary" onClick={saveReview} disabled={submitting}>{submitting ? "Saving..." : "Save Review"}</button>
              {!finalDecision && <button type="button" className="review-action success" onClick={() => openModal("approve")}>Approve</button>}
              {!finalDecision && <button type="button" className="review-action danger" onClick={() => openModal("decline")}>Decline</button>}
              {request.active_project_path && <Link className="review-action secondary link-secondary" to={request.active_project_path}>Open Active Project</Link>}
            </div>
          </aside>
        </section>
      </main>

      {modal === "approve" && <Modal title="Approve Project Request" onClose={closeModal} submitting={submitting} error={modalError} footer={<><button type="button" className="review-modal-cancel" onClick={closeModal} disabled={submitting}>Cancel</button><button type="button" className="review-action success" disabled={submitting} onClick={() => requestAction("approve", { partner_id: nullableId(form.partner_id), project_name: form.project_name || "", internal_note: form.approval_note || controls.review_notes || "", project_work_type: form.project_work_type || controls.project_work_type }, "POST", "Request approved and welcome email sent.")}>{submitting ? "Approving..." : "Approve and Send Welcome Email"}</button></>}><p className="modal-warning success-warning">Approving this request will create or connect the client's portal account, activate the project, and send the client an onboarding email.</p><label htmlFor="approval-work-type">Work type</label><select id="approval-work-type" value={form.project_work_type || ""} onChange={(e) => setForm({ ...form, project_work_type: e.target.value })}><option value="">Select work type</option>{(request.project_work_type_options || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select><label htmlFor="project-name">Project name</label><input id="project-name" value={form.project_name || ""} onChange={(e) => setForm({ ...form, project_name: e.target.value })} /><label htmlFor="approval-partner">Assigned partner</label><select id="approval-partner" value={form.partner_id || ""} onChange={(e) => setForm({ ...form, partner_id: e.target.value })}><option value="">Unassigned</option>{(request.partner_options || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><label htmlFor="approval-note">Internal onboarding note</label><textarea id="approval-note" value={form.approval_note || ""} onChange={(e) => setForm({ ...form, approval_note: e.target.value })} /></Modal>}

      {modal === "decline" && <Modal title="Decline Project Request" onClose={closeModal} submitting={submitting} error={modalError} footer={<><button type="button" className="review-modal-cancel" onClick={closeModal} disabled={submitting}>Cancel</button><button type="button" className="review-action danger" disabled={submitting} onClick={() => requestAction("decline", { internal_reason: form.decline_reason || "", client_message: form.client_message || "" }, "POST", "Request declined and email sent.")}>{submitting ? "Declining..." : "Decline and Send Email"}</button></>}><p className="modal-warning danger-warning">The request will remain in the system, but it will not become an active project. The client will receive a professional decline email.</p><label htmlFor="decline-reason">Internal decline reason <span>Required</span></label><small>Only authorized SCD staff can see this reason.</small><input id="decline-reason" value={form.decline_reason || ""} onChange={(e) => setForm({ ...form, decline_reason: e.target.value })} /><label htmlFor="client-message">Optional message to client</label><small>This will be added to the standard decline email. Do not include internal notes.</small><textarea id="client-message" value={form.client_message || ""} onChange={(e) => setForm({ ...form, client_message: e.target.value })} /></Modal>}
    </div>
  );
}
